/**
 * Lets apps and AI shopping agents change the cart without a page reload.
 *
 * Shopify injects `window.Shopify.actions` on the storefront. After an agent updates the cart,
 * this refreshes the cart drawer, the header cart count and (on the cart page) the cart lines,
 * and opens the drawer when asked. If `Shopify.actions` is missing, nothing happens.
 */
const CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items'];

// Cart elements advertise their sections through getSectionsToRender(); collect them once per section id.
function collectCartSections() {
  const sections = new Map();
  for (const el of document.querySelectorAll(CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }
    for (const entry of entries ?? []) {
      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;
      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;
      sections.set(sectionId, { mount, extractSelector: entry.selector || '.shopify-section' });
    }
  }
  return sections;
}

async function refreshCartUI() {
  const sections = collectCartSections();
  const query = sections.size ? `?sections=${[...sections.keys()].join(',')}` : '';
  const [cart, html] = await Promise.all([
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)),
    // No JSON Accept header here: with it the storefront returns the cart JSON and ignores `sections`.
    sections.size ? fetch(`/cart${query}`).then((r) => (r.ok ? r.json() : null)) : null,
  ]);
  if (!cart) throw new Error('cart fetch failed');

  for (const [id, { mount, extractSelector }] of sections) {
    if (!html?.[id]) continue;
    const source = new DOMParser().parseFromString(html[id], 'text/html').querySelector(extractSelector);
    if (source) mount.innerHTML = source.innerHTML;
  }
  document.querySelector('cart-drawer')?.classList.toggle('is-empty', cart.item_count === 0);
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshCartUI();
      } catch (error) {
        console.error('Cart refresh after an agent update failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}
